'use client';

import React from 'react';
import { ValueValidateInput } from '@/ui/components/shared/inputs/ValueValidateInput';
import { AddedValuePill } from '@/ui/components/shared/inputs/ValueValidateInput/AddedValuePill';
import Drawer from '@/ui/components/shared/Drawer';
import { useCurrencyFormatter } from '@/ui/hooks/useCurrencyFormatter';
import { ArrowLongLeftIcon } from '@heroicons/react/24/outline';
import { useSearchParams } from 'next/navigation';
import { useCurrentVariant } from '@/ui/hooks/productDetail/useCurrentVariant';
import { useOverlayUrlParamSync } from '@/ui/hooks/productDetail/useOverlayUrlParamSync';
import { useNukiIdEntry } from '@/ui/hooks/productDetail/use-nuki-id-entry';
import { useTranslations } from 'next-intl';
import { NOTIFY_ME_PORTAL_TARGET_ID } from '@/utils/constants';
import { useCartStore } from '@/ui/store/cart';
import { BaseProduct } from '@/ui/services/BaseProductService';
import Button from '@/ui/components/shared/Button';
import formatPunctuation from '@/ui/utils/formatPunctuation';
import { useLocalization } from '@/ui/providers/localizationProvider';
import { RichTextFragment } from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { MobileAddToCart } from '@/ui/components/pages/ProductDetail/add-to-cart-bottom/mobile-add-to-cart/mobile-add-to-cart';
import { DesktopAddToCart } from '@/ui/components/pages/ProductDetail/add-to-cart-bottom/desktop-add-to-cart/desktop-add-to-cart';
import { NukiIdSubtext } from './nuki-id-subtext/nuki-id-subtext';

export function NukiIdOverlay({
  apiPath,
  product,
  onlyValidationFlow,
  paramName,
  onNextAction,
  onBackAction,
  allowMultiple = true,
  nukiIds,
  setNukiIds,
  removeParamOnClose = true,
  isValidId,
  nukiIdSubtext,
}: Readonly<{
  apiPath: string;
  product: BaseProduct;
  onlyValidationFlow?: boolean;
  paramName: string;
  allowMultiple?: boolean;
  onNextAction?: () => void;
  onBackAction?: () => void;
  nukiIds: string[];
  setNukiIds: React.Dispatch<React.SetStateAction<string[]>>; // (nukiIds: string[]) => void;
  removeParamOnClose?: boolean;
  isValidId: (id: string, apiPath: string) => Promise<{ valid: boolean; error?: string }>;
  nukiIdSubtext?: RichTextFragment | null;
}>) {
  const t = useTranslations('NukiIdOverlay');
  const { setShowNotifyMe } = useCartStore();

  const { country } = useLocalization();

  const urlParams = useSearchParams();

  const { formatSubscriptionPrice, formatCurrency } = useCurrencyFormatter();

  const { open, closeOverlay } = useOverlayUrlParamSync(paramName, removeParamOnClose);

  // Forward navigation in this flow is React-state driven (no history push), so a bare
  // router.back() jumps out of the flow or no-ops. Collapsing the overlay to the underlying
  // product page is the deterministic "previous step" and preserves cart + configuration.
  const handleBack = () => {
    if (onBackAction) {
      onBackAction();
      return;
    }

    closeOverlay();
  };

  const { currentVariant } = useCurrentVariant(product, true);

  // check if there is some nuki Id in the url param nukiId=asad and prefill it for the value input
  const nukiIdFromUrl = urlParams.get('nukiId') || '';

  const nukiIdEntry = useNukiIdEntry({
    nukiIds,
    setNukiIds,
    sku: currentVariant.sku,
    apiPath,
    isValidId,
    allowMultiple,
  });

  const addToCart = () => {
    if (!currentVariant) {
      return;
    }
    setNukiIds([]);
    closeOverlay();
    product.addToCart(currentVariant, { nukiIds });
  };

  const priceDisplay = () => {
    if (currentVariant?.subscriptionPlan?.periods[0]?.recurring) {
      return formatSubscriptionPrice(currentVariant.subscriptionPlan.periods[0].recurring);
    }

    return (
      currentVariant.priceVariant &&
      formatCurrency(currentVariant.priceVariant.price * (nukiIds.length || 1))
    );
  };

  const defaultOverlayClose = () => {};

  const isProductAvailable = product.isVariantInStock(currentVariant);
  const buttonDisabled = isProductAvailable ? nukiIds.length === 0 : false;

  return (
    <Drawer open={open} rootClass="z-50" overlayClose={defaultOverlayClose}>
      <div className="bg-white px-4 pt-4">
        <div className="flex items-start justify-between">
          <div className="flex h-6 items-center">
            <button
              type="button"
              className="flex items-center gap-2 font-medium"
              onClick={handleBack}
            >
              <ArrowLongLeftIcon className="h-8 w-8 text-primary-100" />
              {t('Back')}
            </button>
          </div>
        </div>
      </div>

      <div className="mt-5 h-full p-4">
        <div className="text-lg ">
          {t.rich('instructions', {
            b: (chunks) =>
              chunks && <span className="font-bold">{formatPunctuation(chunks.toString())}</span>,
            gray: (chunks) => chunks && <span className="text-neutral-50">{chunks}</span>,
          })}
        </div>

        <ValueValidateInput
          onApply={nukiIdEntry.handleApplyNukiId}
          validationError={nukiIdEntry.validationError}
          label="Smart Lock Id"
          buttonText={t('Add')}
          placeholder="Smart Lock ID"
          pending={nukiIdEntry.loading}
          prefillValue={nukiIdFromUrl}
        />
        <NukiIdSubtext subtext={nukiIdSubtext} />
        <div className="mt-2 flex flex-wrap gap-2">
          {nukiIds.map((id, index) => (
            <AddedValuePill
              key={`${id}-${index}`}
              value={id}
              onRemove={nukiIdEntry.handleRemoveNukiId}
              actionLabel={t('Remove Nuki ID')}
            />
          ))}
        </div>
      </div>
      {onlyValidationFlow && onNextAction ? (
        <div className="fixed bottom-0 left-0 flex w-full items-center justify-between gap-3 bg-white px-3 py-2 shadow-nuki-blured">
          <Button type="button" variant="secondary" onClick={handleBack}>
            {t('Back')}
          </Button>
          <Button
            onClick={() => {
              closeOverlay();
              onNextAction();
            }}
            disabled={buttonDisabled}
          >
            {t('Next')}
          </Button>
        </div>
      ) : (
        <>
          <MobileAddToCart
            addToCart={addToCart}
            price={priceDisplay()}
            productName={product.name}
            disabled={buttonDisabled}
            isAvailable={isProductAvailable}
            onNotify={() => setShowNotifyMe(true, currentVariant, null)}
            isUS={country.toUpperCase() === 'US'}
          />
          <DesktopAddToCart
            addToCart={addToCart}
            price={priceDisplay()}
            disabled={buttonDisabled}
            isAvailable={isProductAvailable}
            onNotify={() => setShowNotifyMe(true, currentVariant, null)}
            isUS={country.toUpperCase() === 'US'}
          />
        </>
      )}
      <div id={NOTIFY_ME_PORTAL_TARGET_ID} />
    </Drawer>
  );
}
